// =============================================================================
// MeuDriver — Admin: edição de platform_settings
// =============================================================================

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Settings, Save, Loader2, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { usePlatformSettings } from '@/hooks/usePlatformSettings'

const KEY_LABELS: Record<string, string> = {
  platform_fee_percent: 'Comissão da plataforma (%)',
  payout_window_hours: 'Janela de payout pós-aula (horas)',
  cancellation_full_refund_hours: 'Horas antes da aula para reembolso total',
  cancellation_half_refund_hours: 'Horas antes da aula para reembolso de 50%',
  trial_price_multiplier: 'Multiplicador de preço para aula experimental',
  referral_credit_referrer: 'Crédito de indicação — para quem indicou (R$)',
  referral_credit_referred: 'Crédito de indicação — para quem foi indicado (R$)',
  inactivity_days_for_coupon: 'Dias de inatividade para cupom de reativação',
  reactivation_coupon_percent: 'Desconto do cupom de reativação (%)',
  no_show_charge_percent: 'Percentual cobrado em caso de no-show (%)',
  maintenance_mode: 'Modo manutenção ativo',
  maintenance_message: 'Mensagem de manutenção',
}

// Chaves tratadas com UI dedicada — excluir do grid genérico
const MAINTENANCE_KEYS = ['maintenance_mode', 'maintenance_message'] as const

export default function AdminPlatformSettings() {
  const { data: settings, isLoading } = usePlatformSettings()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Record<string, string>>({})

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase.rpc('update_platform_setting', {
        p_key: key,
        p_value: value,
      })
      if (error) throw error
    },
    onSuccess: (_, { key }) => {
      toast.success('Configuração salva.')
      setEditing((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      queryClient.invalidateQueries({ queryKey: ['platform_settings'] })
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar')
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    )
  }

  const maintenanceMode = settings?.find((s) => s.key === 'maintenance_mode')
  const maintenanceMessage = settings?.find((s) => s.key === 'maintenance_message')
  const isModeActive = maintenanceMode?.value === 'true'
  const otherSettings = (settings ?? []).filter(
    (s) => !(MAINTENANCE_KEYS as ReadonlyArray<string>).includes(s.key),
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Settings className="h-6 w-6 text-gray-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configurações da plataforma</h1>
          <p className="text-sm text-gray-500">Edite os parâmetros globais do MeuDriver.</p>
        </div>
      </div>

      {/* ─── Seção: Modo Manutenção ─────────────────────────────────────────── */}
      <Card className={isModeActive ? 'border-red-300 bg-red-50/50' : ''}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-gray-800">
            <AlertTriangle className={`h-4 w-4 ${isModeActive ? 'text-red-500' : 'text-gray-400'}`} />
            Modo manutenção
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Toggle modo */}
          {maintenanceMode && (
            <div className="flex items-center gap-3">
              <Label htmlFor="maint-toggle" className="text-sm text-gray-700 flex-1">
                {isModeActive ? 'Manutenção ATIVA — banner visível para usuários' : 'Manutenção inativa'}
              </Label>
              <button
                id="maint-toggle"
                role="switch"
                aria-checked={isModeActive}
                onClick={() => {
                  const next = isModeActive ? 'false' : 'true'
                  updateMutation.mutate({ key: 'maintenance_mode', value: next })
                }}
                disabled={updateMutation.isPending}
                data-testid="maintenance-mode-toggle"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  isModeActive ? 'bg-red-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    isModeActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Mensagem */}
          {maintenanceMessage && (
            <div className="space-y-1.5">
              <Label htmlFor="maint-message" className="text-sm text-gray-700">
                Mensagem exibida no banner
              </Label>
              <div className="flex gap-2">
                <Textarea
                  id="maint-message"
                  rows={2}
                  value={editing['maintenance_message'] ?? maintenanceMessage.value}
                  onChange={(e) =>
                    setEditing((prev) => ({ ...prev, maintenance_message: e.target.value }))
                  }
                  data-testid="maintenance-message-input"
                />
                <Button
                  size="sm"
                  disabled={
                    editing['maintenance_message'] === undefined ||
                    editing['maintenance_message'] === maintenanceMessage.value ||
                    updateMutation.isPending
                  }
                  onClick={() =>
                    updateMutation.mutate({
                      key: 'maintenance_message',
                      value: editing['maintenance_message']!,
                    })
                  }
                  data-testid="maintenance-message-save"
                >
                  {updateMutation.isPending && updateMutation.variables?.key === 'maintenance_message' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Grid: demais configurações ─────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2">
        {otherSettings.map((s) => {
          const currentEdit = editing[s.key]
          const isDirty = currentEdit !== undefined && currentEdit !== s.value
          return (
            <Card key={s.key}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-700">
                  {KEY_LABELS[s.key] ?? s.key}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex items-center gap-2">
                <Input
                  type={s.data_type === 'text' ? 'text' : 'number'}
                  step={s.data_type === 'numeric' ? '0.01' : '1'}
                  value={currentEdit ?? s.value}
                  onChange={(e) =>
                    setEditing((prev) => ({ ...prev, [s.key]: e.target.value }))
                  }
                  className="font-mono"
                />
                <Button
                  size="sm"
                  disabled={!isDirty || updateMutation.isPending}
                  onClick={() =>
                    updateMutation.mutate({ key: s.key, value: currentEdit! })
                  }
                >
                  {updateMutation.isPending && updateMutation.variables?.key === s.key ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                </Button>
              </CardContent>
              {s.description && (
                <p className="px-6 pb-4 text-xs text-gray-400">{s.description}</p>
              )}
            </Card>
          )
        })}
      </div>

      <p className="text-xs text-gray-400">
        Última atualização:{' '}
        {settings && settings.length > 0
          ? new Date(
              Math.max(...settings.map((s) => new Date(s.updated_at).getTime()))
            ).toLocaleString('pt-BR')
          : '—'}
      </p>
    </div>
  )
}
